import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import Joi from "joi-browser";
import moment from "moment";
import _ from "lodash";
import Combobox from "react-widgets/Combobox";
import "react-widgets/styles.css";
import cryptoRandomString from "crypto-random-string";
import * as loadingDots from "../../animations/loading-dots.json";
import Table from "../common/Table";
import Pagination from "../common/Pagination";
import Input from "../common/Input";
import Select from "../common/Select";
import BackIcon from "../common/BackIcon";
import ToggleSwitch from "./../common/ToggleSwitch";
import {
  getFastTimeEntryClients,
  getDiagnosisDetails,
  getFosterParentAccounts,
  getServiceItems,
  getProgramUnits,
  getFastTimeEntriesBetweenDates,
  postFastTimeEntry,
} from "../services/soar";
import {
  formatDate,
  getFieldDate,
  getFieldName,
  getFieldText,
  paginate,
  checkIfCommonError,
} from "../../scripts/helperScripts";

const FastTimeEntry = () => {
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  //Route Access Protection
  useEffect(() => {
    if (
      !safyInfo?.user?.modules ||
      !safyInfo.user.safyInfo?.divisions ||
      !safyInfo.user.safyInfo.SOARUserId
    ) {
      navigate("/");
    }

    const modules = safyInfo.user.modules;

    if (
      modules.includes("My Caseload") === false &&
      modules.includes("Team Caseload") === false
    ) {
      navigate("/");
    }
  });

  const [data, setData] = useState({
    Approved: false,
    Entries: [{ _id: cryptoRandomString(10) }],
  });
  const [fastTimeEntries, setFastTimeEntries] = useState([]);
  const [loadStatus, setLoadStatus] = useState({});
  const [inEditMode, setInEditMode] = useState(false);
  const [programUnits, setProgramUnits] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientProgramOptions, setClientProgramOptions] = useState([]);
  const [allServiceItems, setAllServiceItems] = useState([]);
  const [clientDiagnosisOptions, setClientDiagnosisOptions] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const [searchData, setSearchData] = useState({
    CurrentPage: 1,
    StatusFilter: "Open",
    SortColumn: {
      path: "CreatedOn",
      order: "desc",
    },
    ClientSearch: "",
    CaregiverSearch: "",
    StartDate: formatDate(moment().add(-6, "months").toDate()),
    EndDate: formatDate(new Date()),
  });

  const safyUserDivisions = useMemo(() => {
    return safyInfo?.user?.safyInfo?.divisions;
  }, [safyInfo]);

  const safyAzureId = useMemo(() => {
    return safyInfo?.user?.azureId;
  }, [safyInfo]);

  const safyDisplayName = useMemo(() => {
    return safyInfo?.user?.displayName;
  }, [safyInfo]);

  const safyUserCredentials = useMemo(() => {
    return safyInfo?.user?.safyInfo?.SignatureCredentials;
  }, [safyInfo]);

  const loadClientDiagnoses = useCallback((clientProgram) => {
    const controller = new AbortController();
    const signal = controller.signal;

    const load = async () => {
      setLoadStatus((s) => {
        return { ...s, LoadingClientDiagnoses: true };
      });
      const response = await getDiagnosisDetails(clientProgram, signal);

      if (!signal.aborted) {
        if (response) {
          const options = response.map((diagnosis) => {
            return {
              _id: getFieldText(diagnosis.ccx_clientdiagnosis),
              name: `${diagnosis["dx.ccx_code"]} ${diagnosis["dx.ccx_name"]}`,
            };
          });

          setClientDiagnosisOptions(options);

          if (options.length === 1) {
            setData((d) => {
              const obj = { ...d };
              obj.ClientDiagnosis = options[0]._id;
              return obj;
            });
          }
        }

        setLoadStatus((s) => {
          return { ...s, LoadingClientDiagnoses: false };
        });
      }
    };

    load();

    return () => {
      controller.abort();
    };
  }, []);

  const handleViewEntry = async (entryData) => {
    setInEditMode(true);
    const entries = entryData.Services.map((service) => {
      const entry = {
        EntryId: service.EntryId,
        ServiceDate: service.StartDateTime,
        Units: service.Units,
      };

      return entry;
    });

    if (entries.length === 0) {
      entries.push({ _id: cryptoRandomString(10) });
    }

    setData((d) => {
      const obj = {
        ...d,
        GroupId: entryData.GroupId,
        Approved: entryData.FinalSigned,
        Client: entryData.ClientId,
        ClientProgram: entryData.ClientProgramId,
        ServiceItem: `{${entryData.Services[0]?.ServiceItemId}}`,
        ClientDiagnosis: entryData.ClientDiagnosis1Id,
        Caregiver: entryData.CaregiverId,
        Entries: entries,
      };
      return obj;
    });
    setClientProgramOptions([
      {
        _id: entryData.ClientProgramId,
        name: entryData.ClientProgramName,
        ClientProgramName: entryData.ClientProgramName,
        FTEServiceItemIds: ["7E9A2BBB-60B2-EA11-833D-000C295EBA08"],
      },
    ]);
    setAllServiceItems([
      {
        ServiceItemId: `{${entryData.Services[0]?.ServiceItemId}}`,
        ServiceItemName: entryData.Services[0]?.ServiceItemName,
      },
    ]);
    setClientDiagnosisOptions([
      {
        _id: entryData.ClientDiagnosis1Id,
        name: entryData.ClientDiagnosis1Name,
      },
    ]);
    setCaregivers([
      {
        CaregiverId: entryData.CaregiverId,
        CaregiverName: entryData.CaregiverName,
      },
    ]);
  };

  const pageSize = 10;
  const columns = useMemo(() => {
    return searchData.StatusFilter === "Submitted"
      ? [
          {
            key: "Client",
            path: "Client",
            label: "Client",
          },
          {
            key: "Caregiver",
            path: "Caregiver",
            label: "Caregiver",
          },
          {
            key: "CreatedBy",
            path: "CreatedBy",
            label: "Created By",
          },
          {
            key: "CreatedOn",
            path: "CreatedOn",
            label: "Created On",
            content: (group) => moment(group.CreatedOn).format("ll LT"),
          },
          {
            key: "FinalSignedDate",
            path: "FinalSignedDate",
            label: "Submit Date",
            content: (group) => moment(group.FinalSignedDate).format("ll LT"),
          },
          {
            key: "View",
            content: (group) => (
              <i
                className="fas fa-eye"
                onClick={() => {
                  handleViewEntry(group.EntryData);
                }}
              ></i>
            ),
          },
        ]
      : [
          {
            key: "Client",
            path: "Client",
            label: "Client",
          },
          {
            key: "Caregiver",
            path: "Caregiver",
            label: "Caregiver",
          },
          {
            key: "CreatedBy",
            path: "CreatedBy",
            label: "Created By",
          },
          {
            key: "CreatedOn",
            path: "CreatedOn",
            label: "Created On",
            content: (group) => moment(group.CreatedOn).format("ll LT"),
          },
          {
            key: "View",
            content: (group) => (
              <i
                className="fas fa-eye"
                onClick={() => {
                  handleViewEntry(group.EntryData);
                }}
              ></i>
            ),
          },
        ];
  }, [searchData.StatusFilter]);

  // Validation Schema, false indicates field is not saved therefore not validated but present on data obj
  const joiSchema = {
    GroupId: Joi.string(),
    Approved: Joi.boolean(),
    ProgramUnit: Joi.string().allow("", null),
    Client: Joi.string().required(),
    ClientProgram: Joi.string().required(),
    ServiceItem: Joi.string().required(),
    ClientDiagnosis: Joi.string().required(),
    Caregiver: Joi.string().required(),
    Entries: Joi.array()
      .min(1)
      .max(30)
      .items(
        Joi.object({
          EntryId: Joi.string(),
          ServiceDate: Joi.date()
            .max("now")
            .min(new Date("1/1/1900"))
            .allow("")
            .error((errors) => {
              errors.forEach((error) => {
                switch (error.type) {
                  case "date.max":
                    error.message = `Service Date cannot be future dated`;
                    break;
                  case "date.min":
                    error.message = `Service Date cannot be before 1/1/1900`;
                    break;
                  default:
                    return error.message;
                }
              });
              return errors;
            }),
          Units: Joi.number().required(),
        })
      ),
  };

  const caregiverOptions = useMemo(() => {
    const options = caregivers
      .sort((a, b) => {
        if (a.CaregiverName < b.CaregiverName) {
          return -1;
        }
        if (a.CaregiverName > b.CaregiverName) {
          return 1;
        }
        return 0;
      })
      .map((caregiver) => {
        return { _id: caregiver.CaregiverId, name: caregiver.CaregiverName };
      });

    return options;
  }, [caregivers]);

  useEffect(() => {
    document.title = `SAFY Portal - Fast Time Entry`;
    const controller = new AbortController();
    const signal = controller.signal;

    const loadProgramUnits = async () => {
      setLoadStatus((s) => {
        return { ...s, LoadingProgramUnits: true };
      });

      const response = await getProgramUnits("SAFY of South Carolina", signal);

      if (!signal.aborted) {
        if (response !== undefined) {
          const options = response.map((unit) => {
            return { _id: unit.ccx_unitid, name: unit.ccx_name };
          });
          setProgramUnits(options);
        }

        setLoadStatus((s) => {
          return { ...s, LoadingProgramUnits: false };
        });
      }
    };

    const loadCaregivers = async () => {
      setLoadStatus((s) => {
        return { ...s, LoadingCaregivers: true };
      });

      const response = await getFosterParentAccounts(signal);

      if (!signal.aborted) {
        if (response !== undefined) {
          setCaregivers(
            response
              .filter(
                (caregiver) =>
                  getFieldName(caregiver.businessunitid) ===
                  "SAFY of South Carolina"
              )
              .map((caregiver) => {
                return {
                  CaregiverId: caregiver.systemuserid,
                  CaregiverName: caregiver.fullname,
                };
              })
          );
        }

        setLoadStatus((s) => {
          return { ...s, LoadingCaregivers: false };
        });
      }
    };

    loadProgramUnits();
    loadCaregivers();

    return () => {
      controller.abort();
      document.title = "SAFY Portal";
    };
  }, []);

  useEffect(() => {
    if (
      !loadStatus.ClientsLoaded &&
      !loadStatus.LoadingClients &&
      safyUserDivisions
    ) {
      const loadClients = async () => {
        setLoadStatus((s) => {
          return { ...s, LoadingClients: true };
        });

        const divisions = safyUserDivisions
          .filter(
            (division) =>
              division.Active === "1" &&
              division.CompanyName === "SAFY of South Carolina"
          )
          .map((division) => division.DivisionName);

        let response = await getFastTimeEntryClients(divisions); // TODO: figure out how to implement the AbortController to a POST
        const uniqueClientIds = [];
        const options = [];

        response.forEach((item) => {
          const index = uniqueClientIds.indexOf(item.contactid);

          if (index > -1) {
            const option = { ...options[index] };

            option.programs.push({
              ClientProgramId: getFieldText(item.ccx_clientprogram),
              ClientProgramName: getFieldName(item.ccx_clientprogram),
              FacilityName: item["fac.ccx_name"],
              AdmitDate: getFieldDate(item.ccx_admitdate),
              DischargeDate: getFieldDate(item.ccx_dischargedate),
              FTEServiceItemIds: item.fteserviceitemids,
            });

            options[index] = option;
          } else {
            options.push({
              _id: `${item.contactid}`,
              name: `${item.fullname} - ${item.ccx_mpistr}`,
              fullname: item.fullname,
              company: "SAFY of South Carolina",
              programs: [
                {
                  ClientProgramId: getFieldText(item.ccx_clientprogram),
                  ClientProgramName: getFieldName(item.ccx_clientprogram),
                  FacilityName: item["fac.ccx_name"],
                  AdmitDate: getFieldDate(item.ccx_admitdate),
                  DischargeDate: getFieldDate(item.ccx_dischargedate),
                  FTEServiceItemIds: item.fteserviceitemids,
                },
              ],
            });

            uniqueClientIds.push(item.contactid);
          }
        });

        setClientOptions(options);
        setLoadStatus((s) => {
          return { ...s, ClientsLoaded: true, LoadingClients: false };
        });
      };

      loadClients();
    }
  }, [safyUserDivisions, loadStatus.ClientsLoaded, loadStatus.LoadingClients]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (!loadStatus.ServiceItemsLoaded) {
      const loadServiceItems = async () => {
        setLoadStatus((s) => {
          return { ...s, LoadingServiceItems: true };
        });

        const response = await getServiceItems(signal);

        if (!signal.aborted) {
          if (response) {
            setAllServiceItems(
              response.map((item) => {
                return {
                  ServiceItemId: item.ccx_service_itemid,
                  ServiceItemName: item.ccx_name,
                };
              })
            );

            if (response.length === 1) {
              setData((d) => {
                return { ...d, ServiceItem: response[0].ccx_service_itemid };
              });
            }
          }

          setLoadStatus((s) => {
            return {
              ...s,
              ServiceItemsLoaded: true,
              LoadingServiceItems: false,
            };
          });
        }
      };

      loadServiceItems();
    }

    return () => {
      controller.abort();
    };
  }, [loadStatus.ServiceItemsLoaded]);

  const handlePageChange = (page) => {
    const obj = { ...searchData };
    obj.CurrentPage = page;
    setSearchData(obj);
  };

  const handleSort = (sortColumn) => {
    const obj = { ...searchData };
    obj.SortColumn = sortColumn;
    setSearchData(obj);
  };

  const pagedData = useMemo(() => {
    const filtered = fastTimeEntries
      .filter((group) => {
        const status = group.FinalSigned ? "Submitted" : "Open";

        if (status !== searchData.StatusFilter) {
          return false;
        }

        if (
          searchData.ClientSearch &&
          !group.ClientName.toLowerCase().includes(
            searchData.ClientSearch.toLowerCase()
          )
        ) {
          return false;
        }

        if (
          searchData.CaregiverSearch &&
          !group.CareGiverName.toLowerCase().includes(
            searchData.CaregiverSearch.toLowerCase()
          )
        ) {
          return false;
        }

        return true;
      })
      .map((group) => {
        const obj = {
          Client: group.ClientName,
          CreatedBy: group.CreatedByName,
          CreatedOn: moment.utc(group.CreatedOn).toDate(),
          Caregiver: group.CaregiverName,
          FinalSignedDate:
            group.FinalSigned && moment.utc(group.FinalSignedDate).toDate(),
          EntryData: group,
        };
        return obj;
      });

    const sorted = _.orderBy(
      filtered,
      [searchData.SortColumn.path],
      [searchData.SortColumn.order]
    );

    return paginate(sorted, searchData.CurrentPage, pageSize);
  }, [fastTimeEntries, searchData]);

  const handleSearchDataChange = ({ currentTarget: input }) => {
    const obj = { ...searchData };
    obj[input.name] = input.value;
    setSearchData(obj);
  };

  const serviceItemOptions = useMemo(() => {
    if (clientProgramOptions.length === 0 || !data.ClientProgram) {
      return [];
    }

    const selectedProgram = clientProgramOptions.filter(
      (option) => option._id === data.ClientProgram
    )[0];

    const options = allServiceItems
      .filter((item) =>
        selectedProgram.FTEServiceItemIds.map(
          (i) => `{${i.toUpperCase()}}`
        ).includes(item.ServiceItemId)
      )
      .map((item) => {
        return {
          _id: item.ServiceItemId,
          name: item.ServiceItemName,
        };
      });

    if (options.length === 1) {
      setData((d) => {
        const obj = { ...d };
        obj.ServiceItem = options[0]._id;
        return obj;
      });
    }

    return options;
  }, [clientProgramOptions, data.ClientProgram, allServiceItems]);

  const programUnitOptions = useMemo(() => {
    const options = programUnits.filter((unit) => {
      if (!unit.name.includes("Foster Homes")) {
        return false;
      }

      return true;
    });

    return options;
  }, [programUnits]);

  const loadFastTimeEntries = useCallback(
    (programUnit, startDate, endDate) => {
      const controller = new AbortController();
      const signal = controller.signal;

      const load = async () => {
        setLoadStatus((s) => {
          return { ...s, LoadingEntries: true };
        });

        const programUnitName = programUnitOptions.filter(
          (option) => option._id === programUnit
        )[0].name;
        try {
          const response = await getFastTimeEntriesBetweenDates(
            programUnitName,
            startDate,
            endDate,
            signal
          );

          if (!signal.aborted) {
            setFastTimeEntries(response);
            setLoadStatus((s) => {
              return { ...s, LoadingEntries: false };
            });
          }
        } catch (err) {
          // ignore Unauthorized, user needs to refresh authentication token
          if (err.name !== "Unauthorized") {
            console.log(
              `getFastTimeEntriesBetweenDates ${err.name}:`,
              err.message
            );
            // potentially handle more stuff in the future
          }
        }
      };

      load();
      return () => {
        controller.abort();
      };
    },
    [programUnitOptions]
  );

  const handleChange = ({ currentTarget: input }) => {
    const obj = { ...data };
    obj[input.name] = input.value;
    setData(obj);

    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
    }

    setErrors(errs);
    return obj;
  };

  const handleToggleChange = (value) => {
    const obj = { ...searchData };
    obj.StatusFilter = value;
    setSearchData(obj);
  };

  const handleSearchChange = ({ currentTarget: input }) => {
    const obj = { ...searchData };
    obj[input.name] = input.value;
    setSearchData(obj);
  };

  const handleProgramUnitChange = async ({ currentTarget: input }) => {
    handleSearchChange({ currentTarget: input });

    if (!input.value) {
      return;
    }

    loadFastTimeEntries(input.value, searchData.StartDate, searchData.EndDate);
  };

  const handleStartDateChange = async ({ currentTarget: input }) => {
    handleSearchChange({ currentTarget: input });

    if (!input.value) {
      return;
    }

    loadFastTimeEntries(
      searchData.ProgramUnit,
      input.value,
      searchData.EndDate
    );
  };

  const handleEndDateChange = async ({ currentTarget: input }) => {
    handleSearchChange({ currentTarget: input });

    if (!input.value) {
      return;
    }

    loadFastTimeEntries(
      searchData.ProgramUnit,
      searchData.StartDate,
      input.value
    );
  };

  const handleClientChange = ({ currentTarget: input }) => {
    setData((d) => {
      return { ...d, [input.name]: input.value };
    });
    const selectedClient = clientOptions.filter(
      (option) => option._id === input.value
    )[0];

    if (!selectedClient) {
      return;
    }

    const programOptions = selectedClient.programs.map((program) => {
      return {
        _id: program.ClientProgramId,
        name: `${program.FacilityName} (${moment(program.AdmitDate).format(
          "L"
        )}-${
          program.DischargeDate
            ? `${moment(program.DischargeDate).format("L")}`
            : "no date"
        })`,
        ClientProgramName: program.ClientProgramName,
        FTEServiceItemIds: JSON.parse(program.FTEServiceItemIds),
      };
    });

    setClientProgramOptions(programOptions);
  };

  const handleClientProgramChange = async ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    if (input.value) {
      loadClientDiagnoses(input.value);
    }
  };

  const handleEntryChange = ({ currentTarget: input }, index) => {
    const obj = { ...data };
    obj.Entries[index][input.name] = input.value;
    setData(obj);
  };

  const handleDeleteEntry = (index) => {
    const obj = { ...data };
    delete obj.Entries[index];
    setData(obj);
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema, { convert: true });
    return error ? error.details[0].message : null;
  };

  // const validate = (obj) => {
  //   const options = { abortEarly: false };
  //   const { error } = Joi.validate(obj, joiSchema, options);
  //   const validationErrors = {};
  //   if (!error) {
  //     setErrors(validationErrors);
  //     return true;
  //   }

  //   for (let item of error.details)
  //     validationErrors[item.path[0]] = item.message;
  //   setErrors(validationErrors);
  //   console.log("Validation:", validationErrors);
  //   return Object.keys(validationErrors).length === 0;
  // };

  const handleAddNew = () => {
    setData({ Approved: false, Entries: [{ _id: cryptoRandomString(10) }] });
    setInEditMode(true);
  };

  const handleCancel = () => {
    setInEditMode(false);
  };

  const handleApprove = () => {
    handleSaveFTE(true);
    setData((d) => {
      return { ...d, Approved: true };
    });
  };

  const handleSaveFTE = useCallback(
    (finalSigned = false) => {
      const controller = new AbortController();
      const signal = controller.signal;
      const selectedClientOption = clientOptions.filter(
        (option) => option._id === data.Client
      )[0];
      const selectedProgramOption = clientProgramOptions.filter(
        (option) => option._id === data.ClientProgram
      )[0];
      const selectedDiagnosisOption = clientDiagnosisOptions.filter(
        (option) => option._id === data.ClientDiagnosis
      )[0];
      const selectedCaregiverOption = caregiverOptions.filter(
        (option) => option._id === data.Caregiver
      )[0];
      const selectedServiceItemOption = serviceItemOptions.filter(
        (option) => option._id === data.ServiceItem
      )[0];
      const now = moment().format();
      const offset = (moment().utcOffset() / 60) * 100;
      const dateTimeOffset =
        offset >= 1000
          ? `+${offset}`
          : offset > 0
          ? `+0${offset}`
          : offset > -1000
          ? `-0${offset * -1}`
          : `-${offset}`;
      const dateTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const services = data.Entries.map((entry) => {
        const serviceDate = moment(entry.ServiceDate + "Z").toDate();
        const endDate = moment(entry.ServiceDate + "Z")
          .add(entry.Units * 15, "minutes")
          .toDate();

        const service = {
          EntryId: entry.EntryId || undefined,
          Command: entry.EntryId ? "update" : "insert",
          ClientDiagnosis2Id: null,
          ClientDiagnosis2Name: null,
          ClientDiagnosis3Id: null,
          ClientDiagnosis3Name: null,
          ClientDiagnosis4Id: null,
          ClientDiagnosis4Name: null,
          ServiceItemId: selectedServiceItemOption._id,
          ServiceItemName: selectedServiceItemOption.name,
          ServiceStatusId: "54CD33FD-FFBF-E211-90BC-005056822045",
          ServiceStatusName: "Attended",
          DeliveryMethodId: 803080000,
          DeliveryMethodName: "Face-to-Face",
          RecipientCodeId: 229580007,
          RecipientCodeName: "Client and Collaterals",
          PlaceOfServiceId: 229580001,
          PlaceOfServiceName: "Home",
          StartDateTime: serviceDate,
          EndDateTime: endDate,
          Units: entry.Units,
          Minutes: entry.Units * 15,
          CreatedOn: now,
          CreatedById: safyAzureId,
          CreatedByName: safyDisplayName,
          ModifiedOn: now,
          ModifiedById: safyAzureId,
          ModifiedByName: safyDisplayName,
          DateTimeOffset: dateTimeOffset,
          DateTimeZone: dateTimeZone,
          key: entry._id,
        };

        return service;
      });

      const fteObj = {
        GroupId: data.GroupId || undefined,
        Command: data.GroupId ? "update" : "insert",
        GroupType: "fasttimeentry",
        ObjectEnvironment:
          process.env.REACT_APP_ENVIRONMENT === "Production" ? "Live" : "Test",
        ClientId: selectedClientOption._id,
        ClientName: selectedClientOption.fullname,
        ClientProgramId: selectedProgramOption._id,
        ClientProgramName: selectedProgramOption.ClientProgramName,
        ClientDiagnosis1Id: selectedDiagnosisOption._id,
        ClientDiagnosis1Name: selectedDiagnosisOption.name,
        CaregiverId: selectedCaregiverOption._id,
        CaregiverName: selectedCaregiverOption.name,
        FinalSigned: finalSigned,
        FinalSignedDate: finalSigned ? now : null,
        FinalSignedById: finalSigned ? safyAzureId : null,
        FinalSignedByName: finalSigned ? safyDisplayName : null,
        FinalSignedByCredentials: finalSigned ? safyUserCredentials : null,
        CreatedOn: now,
        CreatedById: safyAzureId,
        CreatedByName: safyDisplayName,
        ModifiedOn: now,
        ModifiedById: safyAzureId,
        ModifiedByName: safyDisplayName,
        DateTimeOffset: dateTimeOffset,
        DateTimeZone: dateTimeZone,
        Services: services,
      };

      try {
        if (data.GroupId) {
          const updateEntry = async () => {
            setSaveInProgress(true);
            const response = await postFastTimeEntry(fteObj, signal);

            if (!signal.aborted) {
              setData((d) => {
                const obj = { ...d };
                const entries = response.SystemResponse.splice(1);

                entries.forEach((entry, index) => {
                  obj.Entries[index].EntryId = entry.Id;
                });

                return obj;
              });
              setSaveInProgress(false);
              // loadFastTimeEntries(
              //   searchData.ProgramUnit,
              //   searchData.StartDate,
              //   searchData.EndDate
              // );
            }

            return response;
          };

          toast.promise(updateEntry(), {
            pending: "Updating Fast Time Entry...",
            success: "Fast Time Entry updated",
            error: "An error occurred updating the Fast Time Entry",
          });
        } else {
          const createEntry = async () => {
            setSaveInProgress(true);
            const response = await postFastTimeEntry(fteObj, signal);

            if (!signal.aborted) {
              setData((d) => {
                const obj = { ...d };
                obj.GroupId = response.SystemResponse[0].Id;
                const entries = response.SystemResponse.splice(1);

                entries.forEach((entry, index) => {
                  obj.Entries[index].EntryId = entry.Id;
                });

                return obj;
              });
              setSaveInProgress(false);
              // loadFastTimeEntries(
              //   searchData.ProgramUnit,
              //   searchData.StartDate,
              //   searchData.EndDate
              // );
            }

            return response;
          };

          toast.promise(createEntry(), {
            pending: "Creating Fast Time Entry...",
            success: "Fast Time Entry created",
            error: "An error occurred creating the Fast Time Entry",
          });
        }
      } catch (err) {
        if (!checkIfCommonError(err)) {
          if (err.name === "PostError") {
            // deal with failures
            console.log("handleSaveFTE error:", err);
          } else {
            throw err;
          }
        }
      }

      return () => {
        controller.abort();
      };
    },
    [
      clientOptions,
      clientProgramOptions,
      clientDiagnosisOptions,
      caregiverOptions,
      serviceItemOptions,
      safyAzureId,
      safyDisplayName,
      safyUserCredentials,
      data,
    ]
  );

  return (
    <>
      {inEditMode ? (
        <i
          className="fas fa-arrow-circle-left fa-2x me-2 d-print-none"
          style={{ color: "#f0ad4e" }}
          title="back"
          onClick={handleCancel}
        ></i>
      ) : (
        <BackIcon />
      )}
      {loadStatus.LoadingEntry ? (
        <div className="d-flex justify-content-center align-items-center">
          <h1>Loading Fast Time Entry</h1>
          <Lottie
            animationData={loadingDots.default}
            loop={true}
            autoplay={true}
            rendererSettings={{
              preserveAspectRatio: "xMidYMid slice",
            }}
            height={120}
            width={120}
          />
        </div>
      ) : inEditMode ? (
        <div className="container" style={{ marginBottom: "270px" }}>
          <div className="row">
            <div className="col">
              {/* <Select
                name="Company"
                label="Company:"
                value={data.Company}
                options={companyOptions}
                onChange={handleCompanyChange}
                error={errors.Company}
              /> */}
              Company:
              <br />
              SAFY of South Carolina
            </div>
            <div className="col">
              <label htmlFor="Client">Client:</label>
              <Combobox
                name="Client"
                data={clientOptions}
                dataKey="_id"
                textField="name"
                value={data.Client}
                onChange={(value) => {
                  const e = {
                    currentTarget: {
                      name: "Client",
                      value: value._id || value,
                    },
                  };
                  handleClientChange(e);
                }}
                filter="contains"
                disabled={loadStatus.LoadingClients}
              />
              {errors.Client !== undefined ? (
                <span className="alert alert-danger mt-3">{errors.Client}</span>
              ) : null}
            </div>
            <div className="col">
              <Select
                name="ClientProgram"
                label="Client Program:"
                options={clientProgramOptions}
                value={data.ClientProgram}
                onChange={handleClientProgramChange}
                disabled={loadStatus.LoadingClientPrograms}
                error={errors.ClientProgram}
              />
            </div>
          </div>
          <div className="row">
            <div className="col">
              <Select
                name="ServiceItem"
                label="Service Item:"
                options={serviceItemOptions}
                value={data.ServiceItem}
                onChange={handleChange}
                disabled={loadStatus.LoadingServiceItems}
                error={errors.ServiceItem}
              />
            </div>
            <div className="col">
              <Select
                name="ClientDiagnosis"
                label="Client Diagnosis:"
                options={clientDiagnosisOptions}
                value={data.ClientDiagnosis}
                onChange={handleChange}
                disabled={loadStatus.LoadingClientDiagnoses}
                error={errors.ClientDiagnosis}
              />
            </div>
            <div className="col">
              <label htmlFor="Caregiver">Caregiver:</label>
              <Combobox
                name="Caregiver"
                data={caregiverOptions}
                dataKey="_id"
                textField="name"
                value={data.Caregiver}
                onChange={(value) => {
                  const e = {
                    currentTarget: {
                      name: "Caregiver",
                      value: value._id || value,
                    },
                  };
                  handleChange(e);
                }}
                filter="contains"
                disabled={loadStatus.LoadingCaregivers}
              />
              {errors.Caregiver !== undefined ? (
                <span className="alert alert-danger mt-3">
                  {errors.Caregiver}
                </span>
              ) : null}
            </div>
          </div>
          <div className="row">
            <div className="col">
              Recorded Services (
              {data.ServiceItem !== undefined &&
              data.ServiceItem !== "" &&
              serviceItemOptions.length > 0 ? (
                serviceItemOptions.filter(
                  (option) => option._id === data.ServiceItem
                )[0].name
              ) : (
                <>Service Item</>
              )}
              , Individual, Attended, In Home, Face to Face) ({" "}
              <span
                className={`${
                  data.Entries === undefined || data.Entries.length < 25
                    ? "text-body"
                    : data.Entries < 30
                    ? "text-warning"
                    : "text-danger"
                }`}
              >
                <u>max: 30 entries</u>
              </span>
              )
              <div className="container border-start border-top">
                <div className="row border-bottom">
                  <div className="col py-2 border-end">Service Date</div>
                  <div className="col py-2 border-end">Units</div>
                  <div className="col py-2 border-end">Minutes</div>
                </div>
                {data.Entries.map((item, index) => (
                  <div
                    id={`id_${item._id}`}
                    key={item._id}
                    className="row border-bottom"
                  >
                    <div className="col py-2 border-end d-flex">
                      <i
                        className="far fa-trash-alt me-2 d-print-none"
                        onClick={() => {
                          handleDeleteEntry(index);
                        }}
                      ></i>
                      <Input
                        name="ServiceDate"
                        value={item.ServiceDate}
                        onChange={(e) => {
                          handleEntryChange(e, index);
                        }}
                        disabled={data.Approved}
                        type="datetime-local"
                        max={`${moment().format(
                          moment.HTML5_FMT.DATETIME_LOCAL
                        )}`}
                      />
                    </div>
                    <div className="col py-2 border-end">
                      <Input
                        name="Units"
                        value={item.Units}
                        onChange={(e) => {
                          handleEntryChange(e, index);
                        }}
                        disabled={data.Approved}
                      />
                    </div>
                    <div className="col py-2 border-end">
                      {item.Units !== undefined && item.Units !== ""
                        ? `${item.Units * 15}`
                        : null}
                    </div>
                  </div>
                ))}
              </div>
              {!data.Approved && data.Entries.length < 30 ? (
                <button
                  className="btn btn-primary my-2"
                  onClick={() => {
                    setData((d) => {
                      const obj = { ...d };
                      obj.Entries.push({ _id: cryptoRandomString(10) });
                      return obj;
                    });
                    setTimeout(() => {
                      window.scrollTo(0, document.body.scrollHeight);
                    }, 5);
                  }}
                >
                  Add
                </button>
              ) : null}
            </div>
          </div>
          {errors.Entries !== undefined ? (
            <div className="row">
              <div className="col alert alert-danger">{errors.Entries}</div>
            </div>
          ) : null}
          <div className="row">
            <div className="col pt-2">
              <span className="float-end">
                {data.Approved ? (
                  <button
                    className="btn btn-warning me-2"
                    onClick={handleCancel}
                  >
                    Close
                  </button>
                ) : (
                  <>
                    <button
                      className="btn btn-danger me-2"
                      onClick={handleCancel}
                      disabled={saveInProgress}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary me-2"
                      onClick={() => {
                        handleSaveFTE();
                      }}
                      disabled={saveInProgress}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={handleApprove}
                      disabled={saveInProgress}
                    >
                      Approve
                    </button>
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="container">
          <div className="row">
            <div className="col">
              {/* <Select
                name="Company"
                label="Company:"
                options={companyOptions}
                value={data.Company}
                onChange={handleCompanyChange}
              /> */}
              Company:
              <br />
              SAFY of South Carolina
            </div>
            <div className="col">
              <Select
                name="ProgramUnit"
                label="Program Unit:"
                options={programUnitOptions}
                value={data.ProgramUnit}
                onChange={handleProgramUnitChange}
                disabled={loadStatus.LoadingProgramUnits}
              />
            </div>
            <div className="col pt-3">
              <div className="btn btn-primary mt-3" onClick={handleAddNew}>
                New Fast Time Entry
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <Input
                name="ClientSearch"
                label="Client:"
                value={searchData.ClientSearch}
                onChange={handleSearchDataChange}
                placeholder="Filter by Client..."
              />
            </div>
            <div className="col">
              <Input
                name="CaregiverSearch"
                label="Caregiver:"
                value={searchData.CaregiverSearch}
                onChange={handleSearchDataChange}
                placeholder="Filter by Caregiver..."
              />
            </div>
            <div className="col">
              <Input
                name="StartDate"
                label="Start Date:"
                value={searchData.StartDate}
                onChange={handleStartDateChange}
                type="date"
              />
            </div>
            <div className="col">
              <Input
                name="EndDate"
                label="End Date:"
                value={searchData.EndDate}
                onChange={handleEndDateChange}
                type="date"
              />
            </div>
            <div className="col clearfix pt-3">
              <div className="float-end mt-3">
                <ToggleSwitch
                  values={["Open", "Submitted"]}
                  selected="Open"
                  onChange={handleToggleChange}
                />
              </div>
            </div>
          </div>
          {searchData.ProgramUnit !== undefined &&
          searchData.ProgramUnit !== "" ? (
            loadStatus.LoadingEntries ? (
              <div className="d-flex justify-content-center align-items-center">
                <h1>Loading Fast Time Entries</h1>
                <Lottie
                  animationData={loadingDots.default}
                  loop={true}
                  autoplay={true}
                  rendererSettings={{
                    preserveAspectRatio: "xMidYMid slice",
                  }}
                  height={120}
                  width={120}
                />
              </div>
            ) : fastTimeEntries.length > 0 ? (
              <>
                <Table
                  columns={columns}
                  data={pagedData}
                  sortColumn={searchData.SortColumn}
                  onSort={handleSort}
                  idColumn="ccx_safyfasttimeentryid"
                />
                <div className="row justify-content-center">
                  <Pagination
                    itemsCount={pagedData.length}
                    pageSize={pageSize}
                    currentPage={searchData.CurrentPage}
                    onPageChange={handlePageChange}
                  />
                </div>
              </>
            ) : (
              <div className="h6">No Fast Time Entries found</div>
            )
          ) : null}
        </div>
      )}
    </>
  );
};

export default FastTimeEntry;
